import React from "react"

import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	FormHelperText,
	Grid,
	MenuItem,
	Radio,
	RadioGroup,
	Stack,
	TextField,
	Typography,
} from "@mui/material"
import { i18n } from "@lingui/core"
import { t, Trans } from "@lingui/macro"
import { ErrorResponse, RestResponse } from "@sinossi/mates-react-library"

import {
	AccountType,
	CreateLandlordRequest,
	LandlordCategory,
	PaginatedResponse,
	UserRequest,
	Users,
} from "models"
import { LandlordService } from "services/landlord.service"

type AccountMode = "existing" | "new"

interface LandlordCreateModalProps {
	open: boolean
	isSubmitting: boolean
	onClose: () => void
	onSubmit: (request: CreateLandlordRequest) => void
	onApiError: (response: RestResponse<any, ErrorResponse>) => void
}

interface LandlordCreateModalState {
	category: LandlordCategory
	logo: string
	logoFileName: string | null
	logoError: string | null
	mode: AccountMode
	sendWelcomeEmail: boolean
	accountSearch: string
	accountOptions: Users[]
	accountSearchLoading: boolean
	selectedAccount: Users | null
	newAccount: {
		username: string
		firstName: string
		lastName: string
		address: string
		city: string
		province: string
		postalCode: string
		country: string
		phone: string
		vatCode: string
		fiscalCode: string
	}
	errors: Record<string, string>
}

export class LandlordCreateModal extends React.Component<LandlordCreateModalProps, LandlordCreateModalState> {
	private accountSearchTimeout: ReturnType<typeof setTimeout> | null = null

	constructor(props: LandlordCreateModalProps) {
		super(props)
		this.state = this.getInitialState()
	}

	componentDidUpdate(prevProps: LandlordCreateModalProps) {
		if (!prevProps.open && this.props.open) {
			this.resetFormState()
		}
	}

	componentWillUnmount() {
		if (this.accountSearchTimeout) {
			clearTimeout(this.accountSearchTimeout)
		}
	}

	private getInitialState = (): LandlordCreateModalState => ({
		category: LandlordCategory.OWNER,
		logo: "",
		logoFileName: null,
		logoError: null,
		mode: "existing",
		sendWelcomeEmail: true,
		accountSearch: "",
		accountOptions: [],
		accountSearchLoading: false,
		selectedAccount: null,
		newAccount: {
			username: "",
			firstName: "",
			lastName: "",
			address: "",
			city: "",
			province: "",
			postalCode: "",
			country: "",
			phone: "",
			vatCode: "",
			fiscalCode: "",
		},
		errors: {},
	})

	private resetFormState = () => {
		this.setState(this.getInitialState())
	}

	private handleModeChange = (_event: React.ChangeEvent<HTMLInputElement>, value: string) => {
		this.setState((prev) => ({
			mode: value as AccountMode,
			errors: value === "existing"
				? { ...prev.errors, username: "", firstName: "", lastName: "" }
				: { ...prev.errors, existingAccount: "" },
		}))
	}

	private handleCategoryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ category: event.target.value as LandlordCategory })
	}

	private handleLogoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const input = event.target
		const file = input.files?.[0]
		if (!file) {
			this.setState({
				logo: "",
				logoFileName: null,
				logoError: null,
			})
			input.value = ""
			return
		}

		if (!file.type.startsWith("image/")) {
			this.setState({
				logo: "",
				logoFileName: null,
				logoError: t({ id: "landlord-create.logo.invalid-type", message: "Il file deve essere un'immagine." }),
			})
			input.value = ""
			return
		}

		const reader = new FileReader()
		reader.onloadend = () => {
			if (typeof reader.result !== "string") {
				this.setState({
					logo: "",
					logoFileName: null,
					logoError: t({ id: "landlord-create.logo.error", message: "Impossibile leggere il file selezionato." }),
				})
				return
			}

			const base64 = reader.result.split(",").pop() ?? reader.result
			this.setState({
				logo: base64,
				logoFileName: file.name,
				logoError: null,
			})
		}

		reader.onerror = () => {
			this.setState({
				logo: "",
				logoFileName: null,
				logoError: t({ id: "landlord-create.logo.error", message: "Impossibile leggere il file selezionato." }),
			})
		}

		reader.readAsDataURL(file)
		input.value = ""
	}

	private clearLogo = () => {
		this.setState({
			logo: "",
			logoFileName: null,
			logoError: null,
		})
	}

	private handleAccountSelection = (_event: any, value: Users | null) => {
		this.setState((prev) => ({
			selectedAccount: value,
			errors: { ...prev.errors, existingAccount: "" },
		}))
	}

	private handleAccountSearchInput = (_event: any, value: string) => {
		this.setState({ accountSearch: value })
		if (this.accountSearchTimeout) {
			clearTimeout(this.accountSearchTimeout)
		}
		if (!value || value.trim().length < 3) {
			this.setState({ accountOptions: [], accountSearchLoading: false })
			return
		}
		this.accountSearchTimeout = setTimeout(() => this.fetchAccounts(value.trim()), 400)
	}

	private fetchAccounts = (searchTerm: string) => {
		const request: UserRequest = {
			pageSize: 15,
			pageId: 0,
			orderType: null,
			orderBy: null,
			username: searchTerm,
			firstName: searchTerm,
			lastName: searchTerm,
			city: null,
			accountType: AccountType.LANDLORD,
			disabled: false,
			vatCode: null,
			fiscalCode: null,
		}

		this.setState({ accountSearchLoading: true })
		LandlordService.searchLandlordAccounts(
			request,
			(response: RestResponse<PaginatedResponse<Users>, ErrorResponse>) => {
				if (response.hasError()) {
					this.setState({ accountSearchLoading: false })
					this.props.onApiError(response)
					return
				}

				this.setState({
					accountSearchLoading: false,
					accountOptions: response.data?.items ?? [],
				})
			},
			(response: RestResponse<PaginatedResponse<Users>, ErrorResponse>) => {
				this.setState({ accountSearchLoading: false })
				this.props.onApiError(response)
			}
		)
	}

	private handleNewAccountFieldChange = (field: keyof LandlordCreateModalState["newAccount"], value: string) => {
		this.setState((prev) => ({
			newAccount: {
				...prev.newAccount,
				[field]: value,
			},
			errors: {
				...prev.errors,
				[field]: "",
			},
		}))
	}

	private handleToggleWelcomeEmail = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
		this.setState({ sendWelcomeEmail: checked })
	}

	private getCategoryLabel = (category: LandlordCategory): string => {
		return t({ id: `enums.landlord-type.${category}`, message: category })
	}

	private validateForm = (): boolean => {
		const errors: Record<string, string> = {}
		if (this.state.mode === "existing") {
			if (!this.state.selectedAccount) {
				errors.existingAccount = t({ id: "landlord-create.select-account", message: "Seleziona un account da associare." })
			}
		} else {
			if (!this.state.newAccount.username.trim()) {
				errors.username = t({ id: "landlord-create.username-required", message: "Inserisci l'email dell'account." })
			}
			if (!this.state.newAccount.firstName.trim()) {
				errors.firstName = t({ id: "landlord-create.first-name-required", message: "Il nome è obbligatorio." })
			}
		}

		this.setState({ errors })
		return Object.keys(errors).length === 0
	}

	private normalizeValue = (value: string): string | null => {
		const trimmed = value.trim()
		return trimmed.length > 0 ? trimmed : null
	}

	private handleSubmit = () => {
		if (this.props.isSubmitting) {
			return
		}

		if (!this.validateForm()) {
			return
		}

		const payload: CreateLandlordRequest = {
			category: this.state.category,
			logo: this.normalizeValue(this.state.logo) ?? undefined,
		}

		if (this.state.mode === "existing" && this.state.selectedAccount) {
			payload.accountId = this.state.selectedAccount.id
		} else {
			payload.account = {
				username: this.state.newAccount.username.trim(),
				firstName: this.state.newAccount.firstName.trim(),
				lastName: this.normalizeValue(this.state.newAccount.lastName) ?? undefined,
				address: this.normalizeValue(this.state.newAccount.address) ?? undefined,
				city: this.normalizeValue(this.state.newAccount.city) ?? undefined,
				province: this.normalizeValue(this.state.newAccount.province) ?? undefined,
				postalCode: this.normalizeValue(this.state.newAccount.postalCode) ?? undefined,
				country: this.normalizeValue(this.state.newAccount.country) ?? undefined,
				phone: this.normalizeValue(this.state.newAccount.phone) ?? undefined,
				vatCode: this.normalizeValue(this.state.newAccount.vatCode) ?? undefined,
				fiscalCode: this.normalizeValue(this.state.newAccount.fiscalCode) ?? undefined,
			}
			payload.sendWelcomeEmail = this.state.sendWelcomeEmail
		}

		this.props.onSubmit(payload)
	}

	private getAccountLabel = (account: Users): string => {
		const firstName = account.accountDetails?.firstName ?? ""
		const lastName = account.accountDetails?.lastName ?? ""
		const fullName = `${firstName} ${lastName}`.trim()
		return fullName.length > 0 ? `${account.username} - ${fullName}` : account.username
	}

	render() {
		const logoStatusLabel = this.state.logoFileName
			? i18n._({
				id: "landlord-create.logo.selected",
				message: "File selezionato: {fileName}",
				values: { fileName: this.state.logoFileName ?? "" },
			})
			: t({ id: "landlord-create.logo.placeholder", message: "Nessun file selezionato" })

		return (
			<Dialog open={this.props.open} onClose={this.props.onClose} fullWidth maxWidth="md">
				<DialogTitle>
					<Trans id="landlord-create.title">Crea e associa Landlord</Trans>
				</DialogTitle>
				<DialogContent dividers>
					<Stack spacing={3}>
						<TextField
							select
							fullWidth
							label={t({ id: "landlord-create.category", message: "Categoria" })}
							value={this.state.category}
							onChange={this.handleCategoryChange}
						>
								{(Object.values(LandlordCategory) as LandlordCategory[]).map((category) => (
									<MenuItem value={category} key={category}>
										{this.getCategoryLabel(category)}
									</MenuItem>
								))}
							</TextField>
							<Box>
								<Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
									<Button
										variant="outlined"
										component="label"
										sx={{ textTransform: "none" }}
									>
										<Trans id="landlord-create.logo.upload">Carica logo</Trans>
										<input
											type="file"
											accept="image/*"
											hidden
											onChange={this.handleLogoFileChange}
										/>
									</Button>
									<Box sx={{ flex: 1 }}>
										<Typography variant="body2" color={this.state.logoFileName ? "text.primary" : "text.secondary"}>
											{logoStatusLabel}
										</Typography>
										{this.state.logoFileName && (
											<Button
												size="small"
												color="inherit"
												onClick={this.clearLogo}
												sx={{ textTransform: "none", mt: 0.5 }}
											>
												<Trans id="landlord-create.logo.remove">Rimuovi logo</Trans>
											</Button>
										)}
									</Box>
								</Stack>
								<FormHelperText>
									<Trans id="landlord-create.logo.helper">Carica un'immagine. Verrà convertita automaticamente in Base64.</Trans>
								</FormHelperText>
								{this.state.logoError && (
									<FormHelperText error>{this.state.logoError}</FormHelperText>
								)}
							</Box>
						<Box>
							<Typography variant="subtitle2" sx={{ mb: 1 }}>
								<Trans id="landlord-create.account-mode">Account Landlord</Trans>
							</Typography>
							<RadioGroup row value={this.state.mode} onChange={this.handleModeChange}>
								<FormControlLabel
									value="existing"
									control={<Radio />}
									label={t({ id: "landlord-create.use-existing", message: "Usa account esistente" })}
								/>
								<FormControlLabel
									value="new"
									control={<Radio />}
									label={t({ id: "landlord-create.create-account", message: "Crea nuovo account" })}
								/>
							</RadioGroup>
						</Box>
						{this.state.mode === "existing" ? (
							<Box>
								<Autocomplete
									options={this.state.accountOptions}
									getOptionLabel={this.getAccountLabel}
									loading={this.state.accountSearchLoading}
									value={this.state.selectedAccount}
									onChange={this.handleAccountSelection}
									onInputChange={this.handleAccountSearchInput}
									filterOptions={(options) => options}
									renderInput={(params) => (
										<TextField
											{...params}
											label={t({ id: "landlord-create.account-search", message: "Cerca account (min 3 caratteri)" })}
											error={Boolean(this.state.errors.existingAccount)}
											helperText={
												this.state.errors.existingAccount
													? this.state.errors.existingAccount
													: t({
														id: "landlord-create.account-search-helper",
														message: "Cerca per email, nome o cognome. L'account deve essere di tipo Landlord.",
													})
											}
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<>
														{this.state.accountSearchLoading ? <CircularProgress color="inherit" size={18} /> : null}
														{params.InputProps.endAdornment}
													</>
												),
											}}
										/>
									)}
								/>
							</Box>
						) : (
							<Box>
								<Grid container spacing={2}>
									<Grid item xs={12} sm={6}>
										<TextField
											required
											fullWidth
											label={t({ id: "landlord-create.username", message: "Email account" })}
											value={this.state.newAccount.username}
											onChange={(event) => this.handleNewAccountFieldChange("username", event.target.value)}
											error={Boolean(this.state.errors.username)}
											helperText={this.state.errors.username}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<TextField
											required
											fullWidth
											label={t({ id: "landlord-create.firstName", message: "Nome o ragione sociale" })}
											value={this.state.newAccount.firstName}
											onChange={(event) => this.handleNewAccountFieldChange("firstName", event.target.value)}
											error={Boolean(this.state.errors.firstName)}
											helperText={this.state.errors.firstName}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<TextField
											fullWidth
											label={t({ id: "landlord-create.lastName", message: "Cognome" })}
											value={this.state.newAccount.lastName}
											onChange={(event) => this.handleNewAccountFieldChange("lastName", event.target.value)}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<TextField
											fullWidth
											label={t({ id: "landlord-create.phone", message: "Telefono" })}
											value={this.state.newAccount.phone}
											onChange={(event) => this.handleNewAccountFieldChange("phone", event.target.value)}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<TextField
											fullWidth
											label={t({ id: "landlord-create.address", message: "Indirizzo" })}
											value={this.state.newAccount.address}
											onChange={(event) => this.handleNewAccountFieldChange("address", event.target.value)}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<TextField
											fullWidth
											label={t({ id: "landlord-create.city", message: "Città" })}
											value={this.state.newAccount.city}
											onChange={(event) => this.handleNewAccountFieldChange("city", event.target.value)}
										/>
									</Grid>
									<Grid item xs={12} sm={4}>
										<TextField
											fullWidth
											label={t({ id: "landlord-create.province", message: "Provincia" })}
											value={this.state.newAccount.province}
											onChange={(event) => this.handleNewAccountFieldChange("province", event.target.value)}
										/>
									</Grid>
									<Grid item xs={12} sm={4}>
										<TextField
											fullWidth
											label={t({ id: "landlord-create.postalCode", message: "CAP" })}
											value={this.state.newAccount.postalCode}
											onChange={(event) => this.handleNewAccountFieldChange("postalCode", event.target.value)}
										/>
									</Grid>
									<Grid item xs={12} sm={4}>
										<TextField
											fullWidth
											label={t({ id: "landlord-create.country", message: "Stato" })}
											value={this.state.newAccount.country}
											onChange={(event) => this.handleNewAccountFieldChange("country", event.target.value)}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<TextField
											fullWidth
											label={t({ id: "landlord-create.vatCode", message: "Partita IVA" })}
											value={this.state.newAccount.vatCode}
											onChange={(event) => this.handleNewAccountFieldChange("vatCode", event.target.value)}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<TextField
											fullWidth
											label={t({ id: "landlord-create.fiscalCode", message: "Codice fiscale" })}
											value={this.state.newAccount.fiscalCode}
											onChange={(event) => this.handleNewAccountFieldChange("fiscalCode", event.target.value)}
										/>
									</Grid>
								</Grid>
								<FormHelperText sx={{ mt: 1 }}>
									<Trans id="landlord-create.required-fields">I campi email e nome o ragione sociale sono obbligatori.</Trans>
								</FormHelperText>
								<FormControlLabel
									sx={{ mt: 1 }}
									control={
										<Checkbox
											checked={this.state.sendWelcomeEmail}
											onChange={this.handleToggleWelcomeEmail}
										/>
									}
									label={t({ id: "landlord-create.send-welcome-email", message: "Crea un accesso a Storebook" })}
								/>
							</Box>
						)}
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={this.props.onClose} disabled={this.props.isSubmitting}>
						<Trans id="common.cancel">Annulla</Trans>
					</Button>
					<Button
						variant="contained"
						onClick={this.handleSubmit}
						disabled={this.props.isSubmitting}
						sx={{ textTransform: "none" }}
					>
						{this.props.isSubmitting && <CircularProgress size={18} sx={{ mr: 1, color: "inherit" }} />}
						<Trans id="landlord-create.submit">Crea e associa</Trans>
					</Button>
				</DialogActions>
			</Dialog>
		)
	}
}
